import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useMe } from "../../lib/me";
import { ErrorText } from "../../ui";
import { IconButton } from "../../ui/icons";
import { Conversation } from "../chat/Conversation";
import { useChannel, useMembers, useReadMarkers } from "../chat/data";
import { formatDue } from "../tasks/model";
import { useTaskActions } from "../tasks/use-task-actions";
import { useTask } from "../tasks/use-tasks";
import { changeValue, fieldLabel, type ActivityItem } from "./activity-model";
import { relativeDate } from "./model";

/**
 * An open chat or task row (WP-054). A chat is the conversation itself, as in Chat: the latest
 * messages and a reply box, and reading it here marks it read there. A task shows what changed
 * since the person last looked, then the task.
 */
export function ActivityView({
  item,
  nameOf,
  now,
  onBack,
  onDone,
}: {
  item: ActivityItem;
  nameOf: (uid: string) => string;
  now: Date;
  onBack: () => void;
  onDone: () => void;
}) {
  const back = <IconButton icon="back" label="Back" title="Back (Esc)" className="size-8" onClick={onBack} aria-keyshortcuts="Escape" />;
  const done = <IconButton icon="archive" label="Done" title="Done: out of the inbox (e)" className="size-8" onClick={onDone} aria-keyshortcuts="e" />;
  return item.kind === "chat" ? (
    <ChatPane item={item} back={back} done={done} />
  ) : (
    <TaskPane item={item} nameOf={nameOf} now={now} back={back} done={done} />
  );
}

function ChatPane({ item, back, done }: { item: ActivityItem; back: ReactNode; done: ReactNode }) {
  const me = useMe();
  const channel = useChannel(item.workspaceId, item.targetId);
  const members = useMembers(item.workspaceId);
  const reads = useReadMarkers(item.workspaceId, [item.targetId], me.uid);
  const open = (
    <Link
      to="/w/$workspaceId/chat/$channelId"
      params={{ workspaceId: item.workspaceId, channelId: item.targetId }}
      className="inline-flex min-h-8 items-center rounded-md px-2 text-sm text-accent hover:bg-surface-2"
    >
      Open in Chat
    </Link>
  );
  if (channel.status !== "ready" || channel.data === null) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex h-11 items-center gap-1 border-b border-border">
          {back}
          <span className="flex-1" />
          {done}
        </div>
        {channel.status === "loading" ? <p className="text-sm text-muted">Loading the conversation…</p> : null}
        {channel.status === "error" ? <ErrorText>The conversation could not be loaded: {channel.error.message}</ErrorText> : null}
        {channel.status === "ready" ? <p className="text-sm text-muted">This conversation is gone, or you are no longer in it.</p> : null}
      </div>
    );
  }
  return (
    // The conversation scrolls inside its own box, so it needs a height: the window's, less the page's padding.
    <div className="flex h-[calc(100dvh-6.5rem)] min-h-80 flex-col md:h-[calc(100dvh-3rem)]">
      <Conversation
        key={channel.data.id}
        workspaceId={item.workspaceId}
        channel={channel.data}
        myUid={me.uid}
        members={members.list}
        byUid={members.byUid}
        readAt={reads.get(item.targetId)}
        back={back}
        actions={
          <>
            {open}
            {done}
          </>
        }
      />
    </div>
  );
}

function TaskPane({ item, nameOf, now, back, done }: { item: ActivityItem; nameOf: (uid: string) => string; now: Date; back: ReactNode; done: ReactNode }) {
  const me = useMe();
  const task = useTask(item.workspaceId, item.targetId);
  const actions = useTaskActions();
  const loaded = task.status === "ready" ? task.task : null;
  const nameOrYou = (uid: string) => (uid === me.uid ? "You" : nameOf(uid));
  return (
    <div className="flex flex-col gap-4 pb-16">
      <div className="sticky top-0 z-10 flex h-11 items-center gap-1 border-b border-border bg-bg">
        {back}
        <h1 className="min-w-0 flex-1 truncate px-1 text-base font-semibold text-text">{item.event === "task_assigned" ? "New task for you" : "Task changed"}</h1>
        <Link
          to="/w/$workspaceId/tasks/$taskId"
          params={{ workspaceId: item.workspaceId, taskId: item.targetId }}
          className="inline-flex min-h-8 items-center rounded-md px-2 text-sm text-accent hover:bg-surface-2"
        >
          Open task
        </Link>
        {loaded === null || loaded.status !== "open" ? null : (
          <IconButton icon="task" label="Mark task done" className="size-8" onClick={() => actions.toggle(item.workspaceId, item.targetId, true)} />
        )}
        {done}
      </div>

      <p className="text-sm text-muted">
        {item.event === "task_assigned" ? `${nameOf(item.actorUid)} assigned this to you` : `Changed by ${nameOf(item.actorUid)}`} · {relativeDate(item.lastAt, now)}
      </p>

      {item.changes.length === 0 ? null : (
        <dl aria-label="What changed" className="rounded-xl border border-border bg-surface px-4 py-1">
          {item.changes.map((c) => (
            <div key={c.field} className="grid grid-cols-[6rem_minmax(0,1fr)] items-baseline gap-3 border-b border-border py-2 text-sm last:border-b-0">
              <dt className="text-muted">{fieldLabel(c.field)}</dt>
              <dd className="min-w-0 break-words">
                <del className="text-muted">{changeValue(c.field, c.before, nameOrYou)}</del>
                <span className="text-muted"> → </span>
                <ins className="font-semibold no-underline">{changeValue(c.field, c.after, nameOrYou)}</ins>
              </dd>
            </div>
          ))}
        </dl>
      )}

      {task.status === "loading" ? <p className="text-sm text-muted">Loading the task…</p> : null}
      {task.status === "error" ? <ErrorText>The task could not be loaded: {task.message}</ErrorText> : null}
      {task.status === "missing" ? <p className="text-sm text-muted">This task was deleted, or you can no longer see it.</p> : null}
      {loaded === null ? null : (
        <article className="flex flex-col gap-3">
          <h2 className={loaded.status === "open" ? "text-xl font-semibold text-text" : "text-xl font-semibold text-muted line-through"}>{loaded.title}</h2>
          <dl className="flex flex-col gap-1 text-sm">
            <Prop label="Status">{loaded.status === "open" ? "Open" : loaded.status === "done" ? "Done" : "Cancelled"}</Prop>
            <Prop label="Assignee">{loaded.assigneeUid === null ? "Unassigned" : nameOrYou(loaded.assigneeUid)}</Prop>
            <Prop label="Due">{loaded.due === null ? "No date" : formatDue(loaded.due, now)}</Prop>
            <Prop label="Visibility">{loaded.visibility === "private" ? "Private" : "Shared with the workspace"}</Prop>
            <Prop label="Created by">{nameOrYou(loaded.creatorUid)}</Prop>
          </dl>
          {loaded.notes === "" ? null : <p className="whitespace-pre-line border-t border-border pt-3 text-sm leading-6 text-text">{loaded.notes}</p>}
        </article>
      )}
    </div>
  );
}

function Prop({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] items-baseline gap-2">
      <dt className="text-muted">{label}</dt>
      <dd className="min-w-0 text-text">{children}</dd>
    </div>
  );
}
